/**
 * ErvaPOS — Histórico de Vendas
 * Estética alinhada com ProductsPage / DashboardPage
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { salesAPI, customersAPI } from '../services/api';
import { toast } from 'sonner';
import { buildReceiptPreviewPdfFromElement } from '../lib/receiptPreviewPdf';
import {
  Search, FileText, RotateCcw, ShoppingCart, ChevronLeft, ChevronRight,
  Eye, Download, X, Receipt, Loader2, Calendar, Filter, Mail, Smartphone
} from 'lucide-react';

const PAYMENT_LABELS = {
  cash: 'Dinheiro', card: 'Multibanco/Cartão', mbway: 'MBWay',
  multibanco: 'Multibanco/Cartão', transfer: 'Transferência',
  account_current: 'Conta Corrente',
  credit_card: 'Cartão Crédito', voucher: 'Voucher', other: 'Outro'
};

// Uma fatura criada posteriormente no Fecho do Dia não transforma a venda POS
// numa venda fiscal dentro da FamaPOS.
const isDirectFiscalSale = (sale) =>
  String(sale?.invoice_type || '').toLowerCase() === 'fiscal' &&
  !sale?.fiscal_from_batch &&
  !!sale?.vendus_invoice_id;

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const saleIdFromLookupBarcode = (value) => {
  const code = String(value || '').trim();
  const decimalMatch = code.match(/^S([0-9]{39})$/i);
  const compactMatch = code.match(/^S([0-9A-F]{32})$/i);
  if (!decimalMatch && !compactMatch) return '';

  let raw = '';
  if (decimalMatch) {
    try {
      raw = BigInt(decimalMatch[1]).toString(16).padStart(32, '0');
    } catch (_) {
      return '';
    }
  } else {
    raw = compactMatch[1].toLowerCase();
  }
  return `${raw.slice(0, 8)}-${raw.slice(8, 12)}-${raw.slice(12, 16)}-${raw.slice(16, 20)}-${raw.slice(20)}`;
};

const SalesHistoryPage = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [filterOperator, setFilterOperator] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [saleEmail, setSaleEmail] = useState('');
  const [sendingSaleEmail, setSendingSaleEmail] = useState(false);
  const [salePhone, setSalePhone] = useState('');
  const [sendingSaleSms, setSendingSaleSms] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedSaleId, setCompletedSaleId] = useState(null);
  const [isFiscalSale, setIsFiscalSale] = useState(false);
  const [receiptPdfUrl, setReceiptPdfUrl] = useState('');
  const [receiptPdfLoading, setReceiptPdfLoading] = useState(false);
  const receiptPreviewRef = useRef(null);
  const deepLinkedSaleOpenedRef = useRef('');

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const res = await salesAPI.getAll({ status: 'completed' });
      const data = Array.isArray(res.data) ? res.data : [];
      const sorted = data.sort((a, b) =>
        (b.completed_at || '').localeCompare(a.completed_at || '')
      );
      setSales(sorted);
    } catch (err) {
      console.error('Erro ao carregar vendas:', err);
      toast.error('Erro ao carregar vendas');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  useEffect(() => {
    const q = String(search || '').trim();
    if (q.length < 3) return undefined;
    const timer = window.setTimeout(async () => {
      try {
        const response = await salesAPI.searchVendusHistory(q);
        const historical = Array.isArray(response.data) ? response.data : [];
        if (!historical.length) return;
        setSales((current) => {
          const merged = new Map(current.map((sale) => [sale.id, sale]));
          historical.forEach((sale) => merged.set(sale.id, sale));
          return Array.from(merged.values()).sort((a, b) =>
            String(b.completed_at || b.created_at || '').localeCompare(String(a.completed_at || a.created_at || ''))
          );
        });
      } catch (error) {
        console.error('Erro ao pesquisar arquivo Vendus:', error);
      }
    }, 450);
    return () => window.clearTimeout(timer);
  }, [search]);

  const filtered = sales.filter((s) => {
    const matchesType = filterType === 'all' || String(s?.invoice_type || '').toLowerCase() === filterType;
    const salePayment = String(s?.payment_method || '').toLowerCase();
    const matchesPayment = filterPayment === 'all' || (filterPayment === 'card' ? ['card', 'multibanco', 'credit_card'].includes(salePayment) : salePayment === filterPayment);
    const matchesDate = !filterDate || String(s?.completed_at || '').slice(0, 10) === filterDate;
    const operatorKey = String(s?.operator_id || s?.operator_name || '').trim();
    const matchesOperator = filterOperator === 'all' || operatorKey === filterOperator;

    const q = normalizeSearchText(saleIdFromLookupBarcode(search) || search || '');
    const matchesSearch = !q || normalizeSearchText([
      s?.customer_name,
      s?.customerName,
      s?.ncp_number,
      s?.invoice_number,
      s?.sale_number,
      s?.document_number,
      s?.receipt_number,
      s?.atcud,
      PAYMENT_LABELS[s?.payment_method] || s?.payment_method,
      s?.operator_name,
      s?.type,
      s?.status,
      s?.id,
    ].filter(Boolean).join(' ')).includes(q);

    return matchesType && matchesPayment && matchesDate && matchesOperator && matchesSearch;
  });

  const operators = Array.from(new Map(
    sales
      .filter(s => s?.operator_id || s?.operator_name)
      .map(s => [String(s.operator_id || s.operator_name).trim(), s.operator_name || 'Operador'])
  ).entries()).sort((a, b) => a[1].localeCompare(b[1], 'pt'));
  const filteredTotal = filtered.reduce((sum, sale) => sum + Number(sale.total || 0), 0);
  const filteredAverage = filtered.length ? filteredTotal / filtered.length : 0;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage((current) => Math.min(Math.max(1, current), totalPages));
  }, [totalPages]);

  const paginationItems = (() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const visiblePages = Array.from(new Set([
      1,
      page - 1,
      page,
      page + 1,
      totalPages,
    ].filter((value) => value >= 1 && value <= totalPages))).sort((a, b) => a - b);

    const items = [];
    visiblePages.forEach((pageNumber, index) => {
      if (index > 0 && pageNumber - visiblePages[index - 1] > 1) {
        items.push(`ellipsis-${visiblePages[index - 1]}-${pageNumber}`);
      }
      items.push(pageNumber);
    });
    return items;
  })();


  const loadReceiptBackendPdfForModal = useCallback(async (saleId) => {
    if (!saleId) {
      setReceiptPdfUrl('');
      return;
    }

    setReceiptPdfLoading(true);
    try {
      const response = await salesAPI.getReceiptPDF(saleId, 'receipt');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const nextUrl = window.URL.createObjectURL(blob);

      setReceiptPdfUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return nextUrl;
      });
    } catch (error) {
      console.error('Erro ao carregar pré-visualização PDF do talão:', error);
      setReceiptPdfUrl('');
      toast.error(error?.response?.data?.detail || 'Erro ao carregar pré-visualização do talão');
    } finally {
      setReceiptPdfLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showReceiptModal && completedSaleId) {
      loadReceiptBackendPdfForModal(completedSaleId);
    }

    if (!showReceiptModal) {
      setReceiptPdfUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return '';
      });
      setReceiptPdfLoading(false);
    }
  }, [showReceiptModal, completedSaleId, loadReceiptBackendPdfForModal]);

  const handleViewFiscalPDF = async (sale, format = 'a4') => {
    setPdfLoading(sale.id + '-' + format);
    try {
      const res = await salesAPI.getInvoicePDF(sale.id, format);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (err) {
      toast.error('Erro ao gerar PDF fiscal');
    }
    setPdfLoading(null);
  };

  const buildReceiptPreviewPdfPayload = async () => {
    const docNumber =
      (isDirectFiscalSale(receiptData?.sale) ? receiptData?.sale?.vendus_invoice_number : null) ||
      receiptData?.sale?.ncp_number ||
      completedSaleId ||
      'talao';

    return await buildReceiptPreviewPdfFromElement(
      receiptPreviewRef.current,
      docNumber
    );
  };

  const sendReceiptPreviewEmail = async (saleId, recipientEmail) => {
    const email = String(recipientEmail || '').trim();

    if (!saleId) {
      throw new Error('Venda sem identificador');
    }

    if (!email) {
      throw new Error('Indique um email');
    }

    // Blindagem: o anexo do talão é sempre gerado no backend.
    // Nunca usamos PDF criado a partir da pré-visualização HTML do frontend.
    await salesAPI.sendEmail(saleId, email);
  };

  const loadSaleIntoReceiptModal = async (sale, fallbackEmail = '') => {
    if (!sale?.id) {
      throw new Error('Venda sem identificador');
    }

    const receiptRes = await salesAPI.getReceipt(sale.id);
    const receipt = receiptRes?.data || null;

    setReceiptData(receipt);
    setCompletedSaleId(sale.id);
    setSaleEmail(
      receipt?.sale?.customer_email ||
      sale?.customer_email ||
      fallbackEmail ||
      ''
    );
    setIsFiscalSale(isDirectFiscalSale(receipt?.sale));
    setShowReceiptModal(true);

    await new Promise((resolve) => setTimeout(resolve, 300));
    return receipt;
  };

  const openReceiptPreviewPrintWindow = async () => {
    if (!completedSaleId) {
      throw new Error('Venda sem identificador');
    }

    // Blindagem: impressão sempre pelo PDF backend 80mm.
    const response = await salesAPI.getReceiptPDF(completedSaleId, 'receipt');
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const win = window.open(blobUrl, '_blank');

    if (!win) {
      throw new Error('O navegador bloqueou a abertura da janela de impressão');
    }

    toast.success('Talão backend pronto a imprimir');
  };

  const downloadReceiptPreviewPdf = async () => {
    if (!completedSaleId) {
      throw new Error('Venda sem identificador');
    }

    // Blindagem: descarregar/abrir sempre o PDF backend 80mm.
    const response = await salesAPI.getReceiptPDF(completedSaleId, 'receipt');
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  const handleDownloadPDF = async (sale) => {
    setPdfLoading(sale.id + '-dl');
    try {
      const res = isDirectFiscalSale(sale)
        ? await salesAPI.getInvoicePDF(sale.id, 'a4')
        : await salesAPI.getReceiptPDF(sale.id, 'a4');
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ((isDirectFiscalSale(sale) ? 'Fatura_' : 'Documento_') +
        (isDirectFiscalSale(sale) ? sale.vendus_invoice_number : sale.ncp_number || sale.id) + '.pdf').replace(/[\s/]/g, '_');
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descarregado');
    } catch (err) {
      toast.error('Erro ao descarregar PDF');
    }
    setPdfLoading(null);
  };

  const handleViewInternalPDF = async (sale, format = 'receipt') => {
    setPdfLoading(sale.id + '-internal-' + format);
    try {
      const res = await salesAPI.getReceiptPDF(sale.id, format === 'receipt' ? 'receipt' : format);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (err) {
      console.error('Erro ao gerar PDF interno:', err);
      toast.error('Erro ao gerar PDF interno');
    }
    setPdfLoading(null);
  };

  const handleViewReceiptModal = async (sale) => {
    setPdfLoading(sale.id + '-receipt-modal');
    try {
      await loadSaleIntoReceiptModal(sale, saleEmail || sale?.customer_email || '');
    } catch (err) {
      console.error('Erro ao abrir talão novo:', err);
      toast.error('Erro ao abrir talão');
    }
    setPdfLoading(null);
  };

  const handleCopyToCart = (sale) => {
    const items = (sale.items || []).map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      unit_price: item.unit_price,
      quantity: item.quantity,
      barcode: item.barcode || '',
      product_reference: item.product_reference || item.reference || '',
      product_barcode: item.product_barcode || item.barcode || '',
      batch_id: item.batch_id || '',
      batch_qr_code: item.batch_qr_code || '',
      lot_number: item.lot_number || '',
      batch_expiry_date: item.batch_expiry_date || item.expiry_date || '',
      vat_rate: item.vat_rate || 23,
      notes: item.notes || '',
      provider_id: item.provider_id || null,
      provider_name: item.provider_name || null,
      provider_specialty: item.provider_specialty || null,
    }));
    sessionStorage.setItem('copyToCart', JSON.stringify(items));
    toast.success('Itens copiados — a abrir POS');
    navigate('/pos');
  };

  const openSaleDetail = async (sale) => {
    try {
      let fullSale = sale || null;
      let resolvedEmail = String(sale?.customer_email || '').trim();
      let resolvedPhone = String(sale?.customer_phone || '').trim();

      if (sale?.id) {
        const saleRes = await salesAPI.getById(sale.id);
        fullSale = saleRes?.data || sale || null;
      }

      if (!resolvedEmail && fullSale?.customer_email) {
        resolvedEmail = String(fullSale.customer_email || '').trim();
      }

      if (!resolvedPhone && fullSale?.customer_phone) {
        resolvedPhone = String(fullSale.customer_phone || '').trim();
      }

      if ((!resolvedEmail || !resolvedPhone) && fullSale?.customer_id && customersAPI?.getById) {
        try {
          const customerRes = await customersAPI.getById(fullSale.customer_id);
          if (!resolvedEmail) {
            resolvedEmail = String(customerRes?.data?.email || '').trim();
          }
          if (!resolvedPhone) {
            resolvedPhone = String(
              customerRes?.data?.mobile ||
              customerRes?.data?.phone ||
              customerRes?.data?.telephone ||
              ''
            ).trim();
          }
        } catch (err) {
          console.error('Erro a resolver contactos do cliente:', err);
        }
      }

      setSelectedSale(fullSale);
      setSaleEmail(resolvedEmail);
      setSalePhone(resolvedPhone);
      setShowDetail(true);
    } catch (error) {
      console.error('Erro ao abrir detalhe da venda:', error);
      setSelectedSale(sale || null);
      setSaleEmail(String(sale?.customer_email || '').trim());
      setSalePhone(String(sale?.customer_phone || '').trim());
      setShowDetail(true);
    }
  };

  useEffect(() => {
    const saleId = new URLSearchParams(window.location.search).get('sale_id');
    if (!saleId || deepLinkedSaleOpenedRef.current === saleId || !sales.length) return;
    const sale = sales.find((candidate) => String(candidate?.id) === saleId);
    if (!sale) return;
    deepLinkedSaleOpenedRef.current = saleId;
    openSaleDetail(sale);
  }, [sales]); // openSaleDetail usa apenas o registo já carregado e resolve o detalhe pela API.

  const handleSendSaleEmail = async (sale) => {
    const email = String(saleEmail || '').trim();

    if (!sale?.id) {
      toast.error('Venda sem identificador');
      return;
    }

    if (!email) {
      toast.error('Indique um email para envio');
      return;
    }

    setSendingSaleEmail(true);
    try {
      await loadSaleIntoReceiptModal(sale, email);
      await sendReceiptPreviewEmail(sale.id, email);
      toast.success('Talão enviado por email com sucesso');
    } catch (err) {
      console.error('Erro ao enviar talão por email:', err);
      toast.error(err?.response?.data?.detail || err?.message || 'Erro ao enviar talão por email');
    } finally {
      setSendingSaleEmail(false);
    }
  };

  const handleSendSaleSms = async (sale) => {
    const phone = String(salePhone || '').trim();

    if (!sale?.id) {
      toast.error('Venda sem identificador');
      return;
    }

    if (!phone) {
      toast.error('Indique o telemóvel do cliente');
      return;
    }

    setSendingSaleSms(true);
    try {
      const response = await salesAPI.sendReceiptSms(sale.id, phone);
      if (response?.data?.queued_local_only) {
        toast.warning('Telemóvel SMS temporariamente offline. A mensagem ficou guardada para reenvio.');
      } else {
        toast.success('Link público do talão enviado por SMS. Disponível durante 3 meses.');
      }
    } catch (err) {
      console.error('Erro ao enviar talão por SMS:', err);
      toast.error(err?.response?.data?.detail || 'Erro ao enviar talão por SMS');
    } finally {
      setSendingSaleSms(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-PT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  const hasFilters = search || filterType !== 'all' || filterPayment !== 'all' || filterOperator !== 'all' || filterDate;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Histórico de Vendas</h1>
          <p className="text-slate-400">{filtered.length} vendas encontradas</p>
        </div>
        <Button onClick={fetchSales} variant="outline" className="border-slate-700" disabled={loading}>
          <RotateCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-dark"><CardContent className="p-4"><p className="text-xs text-slate-400">Número de vendas</p><p className="text-2xl font-bold text-white">{filtered.length}</p></CardContent></Card>
        <Card className="card-dark"><CardContent className="p-4"><p className="text-xs text-slate-400">Total vendido</p><p className="text-2xl font-bold text-emerald-400">€{filteredTotal.toFixed(2)}</p></CardContent></Card>
        <Card className="card-dark"><CardContent className="p-4"><p className="text-xs text-slate-400">Venda média</p><p className="text-2xl font-bold text-white">€{filteredAverage.toFixed(2)}</p></CardContent></Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Ler código da venda ou pesquisar NCP, fatura, cliente..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 bg-slate-800/50 border-slate-700"
          />
        </div>
        <select
          value={filterOperator}
          onChange={e => { setFilterOperator(e.target.value); setPage(1); }}
          className="bg-slate-800/50 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="all">Todos os operadores</option>
          {operators.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select
          value={filterType}
          onChange={e => { setFilterType(e.target.value); setPage(1); }}
          className="bg-slate-800/50 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="all">Todos os tipos</option>
          <option value="fiscal">Fiscal (Vendus)</option>
          <option value="internal">Interno (NCP)</option>
        </select>
        <select
          value={filterPayment}
          onChange={e => { setFilterPayment(e.target.value); setPage(1); }}
          className="bg-slate-800/50 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="all">Todos pagamentos</option>
          {Object.entries(PAYMENT_LABELS).filter(([k]) => !['multibanco', 'credit_card'].includes(k)).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <Input
          type="date"
          value={filterDate}
          onChange={e => { setFilterDate(e.target.value); setPage(1); }}
          className="bg-slate-800/50 border-slate-700 w-44"
        />
        {hasFilters && (
          <Button
            variant="ghost" size="icon"
            onClick={() => { setSearch(''); setFilterType('all'); setFilterPayment('all'); setFilterOperator('all'); setFilterDate(''); setPage(1); }}
            className="text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Tabela */}
      <Card className="card-dark">
        <Table className="table-dark">
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Documento</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Operador</TableHead>
              <TableHead>Pagamento</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-emerald-400" />
                  <p className="text-slate-400">A carregar vendas...</p>
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-500">Nenhuma venda encontrada</p>
                </TableCell>
              </TableRow>
            ) : paginated.map(sale => (
              <TableRow key={sale.id} className="hover:bg-slate-800/40 cursor-pointer" onClick={() => openSaleDetail(sale)}>
                <TableCell className="text-slate-300 whitespace-nowrap text-sm">
                  {formatDate(sale.completed_at)}
                </TableCell>
                <TableCell>
                  {sale.historical_vendus ? (
                    <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">Histórico Vendus</Badge>
                  ) : isDirectFiscalSale(sale) ? (
                    <span className="text-emerald-400 font-mono font-medium text-sm">{sale.vendus_invoice_number}</span>
                  ) : (
                    <span className="text-blue-400 font-mono text-sm">{sale.ncp_number || '-'}</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-300 text-sm">
                  {sale.customer_name || 'Consumidor Final'}
                </TableCell>
                <TableCell className="text-slate-300 text-sm">
                  {sale.operator_name || 'Desconhecido'}
                </TableCell>
                <TableCell className="text-slate-400 text-sm">
                  {PAYMENT_LABELS[sale.payment_method] || sale.payment_method || '-'}
                </TableCell>
                <TableCell className="text-right font-mono font-medium text-white">
                  €{(sale.total || 0).toFixed(2)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                  {isDirectFiscalSale(sale) ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30">Fiscal</Badge>
                  ) : (
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30">Interno</Badge>
                  )}
                  {sale.has_returns && (
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                      {sale.return_count} devolução{sale.return_count === 1 ? '' : 'ões'} · €{Number(sale.returned_total || 0).toFixed(2)}
                    </Badge>
                  )}
                  </div>
                </TableCell>
                <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white"
                      onClick={() => openSaleDetail(sale)} title="Ver detalhes">
                      <Eye className="w-4 h-4" />
                    </Button>
                    {isDirectFiscalSale(sale) && !sale.historical_vendus && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-400 hover:text-emerald-300"
                        onClick={() => handleViewFiscalPDF(sale)} disabled={pdfLoading === sale.id + '-a4'} title="PDF Fiscal">
                        <FileText className={`w-4 h-4 ${pdfLoading === sale.id + '-a4' ? 'animate-pulse' : ''}`} />
                      </Button>
                    )}
                    {!sale.historical_vendus && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-blue-400"
                        onClick={() => handleCopyToCart(sale)} title="Copiar para carrinho">
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Paginação */}
        <div className="flex flex-col gap-3 px-4 py-3 border-t border-slate-700/50 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-400">Página {page} de {totalPages}</span>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              Linhas por página
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                className="h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-white"
                aria-label="Linhas por página"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1} className="border-slate-700 h-8 w-8 p-0" title="Página anterior">
                <ChevronLeft className="w-4 h-4" />
              </Button>

              {paginationItems.map((item) => typeof item === 'number' ? (
                <Button
                  key={item}
                  variant={item === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(item)}
                  className={item === page
                    ? 'h-8 min-w-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700'
                    : 'h-8 min-w-8 border-slate-700 px-2'}
                  aria-current={item === page ? 'page' : undefined}
                  title={`Página ${item}`}
                >
                  {item}
                </Button>
              ) : (
                <span key={item} className="px-1 text-slate-500" aria-hidden="true">…</span>
              ))}

              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages} className="border-slate-700 h-8 w-8 p-0" title="Página seguinte">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Modal Detalhes */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedSale?.historical_vendus ? (
                <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">Histórico Vendus</Badge>
              ) : isDirectFiscalSale(selectedSale) ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Fiscal</Badge>
              ) : (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Interno</Badge>
              )}
              {(isDirectFiscalSale(selectedSale) ? selectedSale?.vendus_invoice_number : selectedSale?.ncp_number) || 'Detalhes da Venda'}
            </DialogTitle>
          </DialogHeader>

          {selectedSale && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm bg-slate-800/50 rounded-lg p-4">
                <div><span className="text-slate-400">Data:</span><span className="ml-2 text-white">{formatDate(selectedSale.completed_at)}</span></div>
                <div><span className="text-slate-400">Pagamento:</span><span className="ml-2 text-white">{PAYMENT_LABELS[selectedSale.payment_method] || selectedSale.payment_method}</span></div>
                <div><span className="text-slate-400">Cliente:</span><span className="ml-2 text-white">{selectedSale.customer_name || 'Consumidor Final'}</span></div>
                {isDirectFiscalSale(selectedSale) && selectedSale.vendus_invoice_number && (
                  <div><span className="text-slate-400">Fatura:</span><span className="ml-2 text-emerald-400 font-mono">{selectedSale.vendus_invoice_number}</span></div>
                )}
              </div>

              {selectedSale.has_returns && (
                <div className="rounded-lg border border-amber-700/50 bg-amber-950/20 p-3 text-sm">
                  <p className="font-medium text-amber-300 mb-2">Devoluções associadas à venda original</p>
                  {(selectedSale.returns || []).map((ret) => (
                    <div key={ret.id || ret.return_number} className="flex justify-between gap-3 text-slate-300">
                      <span>{ret.return_number}{ret.vendus_credit_note_number ? ` · NC ${ret.vendus_credit_note_number}` : ''}</span>
                      <span>-€{Number(ret.total_refund || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              <Card className="card-dark">
                <Table className="table-dark">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artigo</TableHead>
                      <TableHead className="text-center">Qtd</TableHead>
                      <TableHead className="text-right">Preço</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedSale.items || []).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-white text-sm">{item.product_name}</TableCell>
                        <TableCell className="text-center text-slate-300">{item.quantity}</TableCell>
                        <TableCell className="text-right text-slate-300">€{(item.unit_price || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right text-white font-medium">€{(item.total || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>

              <div className="space-y-1 text-sm px-2">
                {(selectedSale.discount_total || 0) > 0 && (
                  <div className="flex justify-between text-red-400"><span>Descontos:</span><span>-€{selectedSale.discount_total.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between text-slate-300"><span>IVA:</span><span>€{(selectedSale.vat_total || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-lg font-bold text-white border-t border-slate-600 pt-2 mt-2"><span>TOTAL:</span><span>€{(selectedSale.total || 0).toFixed(2)}</span></div>
              </div>
            </div>
          )}


          {selectedSale && !selectedSale.historical_vendus && (
            <div className="space-y-3 pt-2">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Enviar por email</label>
                <Input
                  type="email"
                  value={saleEmail}
                  onChange={(e) => setSaleEmail(e.target.value)}
                  placeholder="Email do cliente"
                  className="bg-slate-800/60 border-slate-700 text-white"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Se o cliente tiver email registado, aparece preenchido automaticamente.
                </p>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">Enviar link do talão por SMS</label>
                <Input
                  type="tel"
                  value={salePhone}
                  onChange={(e) => setSalePhone(e.target.value)}
                  placeholder="Telemóvel do cliente"
                  className="bg-slate-800/60 border-slate-700 text-white"
                />
                <p className="text-xs text-slate-500 mt-2">
                  O link abre diretamente o talão e expira ao fim de três meses.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2 pt-2">
            {selectedSale?.historical_vendus ? (
              <div className="mr-auto rounded-md border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-sm text-violet-200">
                Documento apenas para consulta. Não altera stock, caixa, relatórios ou pontos.
              </div>
            ) : isDirectFiscalSale(selectedSale) ? (
              <>
                <Button onClick={() => handleViewFiscalPDF(selectedSale)} className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!!pdfLoading}>
                  <FileText className="w-4 h-4 mr-2" />Ver Fatura
                </Button>
                <Button variant="outline" onClick={() => handleViewReceiptModal(selectedSale)}
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" disabled={!!pdfLoading}>
                  <Receipt className="w-4 h-4 mr-2" />Talão
                </Button>
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedSale)}
                  className="border-slate-700" disabled={!!pdfLoading}>
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => handleViewReceiptModal(selectedSale)} className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!!pdfLoading}>
                  <Receipt className="w-4 h-4 mr-2" />Imprimir Talão
                </Button>
                <Button variant="outline" onClick={() => handleViewInternalPDF(selectedSale, 'a4')}
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" disabled={!!pdfLoading}>
                  <FileText className="w-4 h-4 mr-2" />PDF A4
                </Button>
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedSale)}
                  className="border-slate-700" disabled={!!pdfLoading}>
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
              </>
            )}
            {!selectedSale?.historical_vendus && <Button
              variant="outline"
              onClick={() => handleSendSaleEmail(selectedSale)}
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              disabled={sendingSaleEmail}
            >
              {sendingSaleEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />Enviar por email
                </>
              )}
            </Button>}
            {!selectedSale?.historical_vendus && <Button
              variant="outline"
              onClick={() => handleSendSaleSms(selectedSale)}
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
              disabled={sendingSaleSms}
            >
              {sendingSaleSms ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...
                </>
              ) : (
                <>
                  <Smartphone className="w-4 h-4 mr-2" />Enviar por SMS
                </>
              )}
            </Button>}
            {!selectedSale?.historical_vendus && <Button variant="outline" className="border-slate-700"
              onClick={() => { handleCopyToCart(selectedSale); setShowDetail(false); }}>
              <ShoppingCart className="w-4 h-4 mr-2" />Copiar Carrinho
            </Button>}
            <Button variant="outline" onClick={() => setShowDetail(false)} className="border-slate-700">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="modal-light w-[min(96vw,640px)] max-w-[640px] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-400" />
              Talão de Venda
            </DialogTitle>
          </DialogHeader>

          {receiptPdfUrl ? (
            <div className="bg-white rounded-lg overflow-hidden mx-auto border border-slate-200" style={{ width: '88mm', maxWidth: '100%', height: '72vh' }}>
              {/* Pré-visualização oficial do talão backend: igual ao PDF de impressão/email */}
              <iframe
                title="Pré-visualização do talão PDF"
                src={receiptPdfUrl}
                className="w-full h-full border-0 bg-white"
              />
            </div>


          ) : receiptPdfLoading ? (
            <div className="bg-white rounded-lg mx-auto border border-slate-200 flex flex-col items-center justify-center gap-3" style={{ width: '88mm', maxWidth: '100%', height: '72vh' }}>
              <Loader2 className="w-7 h-7 animate-spin text-emerald-600" />
              <p className="text-sm text-slate-700">A carregar talão oficial...</p>
            </div>


          ) : receiptData && (
            <div ref={receiptPreviewRef} className="bg-white text-black p-4 rounded-lg font-mono text-sm w-[80mm] mx-auto" id="receipt-content">
              <div className="text-center mb-3">
                {receiptData.company?.logo_url && (
                  <img
                    src={receiptData.company.logo_url}
                    alt="Logo"
                    className="h-14 mx-auto mb-2 object-contain"
                  />
                )}
                <p className="font-bold text-[17px] leading-tight whitespace-nowrap tracking-[-0.2px]">{receiptData.company?.name || 'Ervanária'}</p>
                {receiptData.company?.address && (
                  <p className="text-[11px] leading-tight text-gray-800">{receiptData.company.address}</p>
                )}
                {receiptData.company?.nif && (
                  <p className="text-[11px] leading-tight text-gray-800">NIF: {receiptData.company.nif}</p>
                )}
                {(receiptData.company?.phone || receiptData.company?.email || receiptData.company?.website) && (
                  <p className="text-[11px] leading-tight text-gray-800">
                    {receiptData.company.phone && `Tel: ${receiptData.company.phone}`}
                    {receiptData.company.phone && receiptData.company.email && ' | '}
                    {receiptData.company.email}
                    {(receiptData.company.phone || receiptData.company.email) && receiptData.company.website && ' | '}
                    {receiptData.company.website}
                  </p>
                )}
                {receiptData.company?.capital && (
                  <p className="text-[11px] leading-tight text-gray-800">
                    Capital Social: €{receiptData.company.capital}
                  </p>
                )}
              </div>

              <div className="border-t border-b border-gray-300 py-2 my-2 text-left">
                <p className="text-[11px] font-medium text-black">
                  {isDirectFiscalSale(receiptData.sale) ? 'Fatura Venda' : 'Venda Interna'} {isDirectFiscalSale(receiptData.sale) ? receiptData.sale?.vendus_invoice_number : receiptData.sale?.ncp_number || '-'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
                <div className="leading-tight">
                  <div className="text-gray-700">Data:</div>
                  <div className="text-black font-medium">
                    {receiptData.sale?.completed_at ? new Date(receiptData.sale.completed_at).toLocaleDateString('pt-PT') : '-'}
                  </div>
                </div>
                <div className="leading-tight text-right">
                  <div className="text-gray-700">Op:</div>
                  <div className="text-black font-medium">{receiptData.sale?.operator_name || '-'}</div>
                </div>
              </div>

              <div className="text-[11px] mb-2 leading-tight">
                <div><span className="text-gray-700">Cliente:</span> <span className="font-medium text-black">{receiptData.sale?.customer_name || 'Consumidor Final'}</span></div>
                <div><span className="text-gray-700">Saldo Pontos Conta Cliente:</span> <span className="font-medium text-black">{receiptData.sale?.customer_points ?? '-'}</span></div>
              </div>

              <div className="border-t border-gray-300 pt-2 mt-2">
                <table className="w-full text-[11px] table-fixed">
                  <colgroup>
                    <col style={{ width: '48%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-gray-300 text-gray-700">
                      <th className="text-left py-1 font-bold">Artigo</th>
                      <th className="text-right py-1 font-bold">Qtd</th>
                      <th className="text-right py-1 font-bold">Preço</th>
                      <th className="text-right py-1 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiptData.sale?.items?.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100 align-top">
                        <td className="py-1 pr-2 text-black break-words">
                          <div>{item.product_name}</div>
                          {(item.lot_number || item.batch_expiry_date || item.expiry_date) && (
                            <div className="text-[9px] text-gray-600 leading-tight mt-0.5 whitespace-nowrap">
                              {item.lot_number ? `Lote: ${item.lot_number}` : ''}
                              {item.lot_number && (item.batch_expiry_date || item.expiry_date) ? ' | ' : ''}
                              {(item.batch_expiry_date || item.expiry_date) ? `Val: ${item.batch_expiry_date || item.expiry_date}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="text-right py-1 text-black">{item.quantity}</td>
                        <td className="text-right py-1 text-black">€{(item.unit_price || 0).toFixed(2)}</td>
                        <td className="text-right py-1 text-black font-medium">€{(item.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-t border-gray-300 pt-2 mt-2 space-y-1 text-[11px]">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>€{(receiptData.sale?.subtotal || 0).toFixed(2)}</span>
                </div>

                {(receiptData.sale?.discount_total || 0) > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>Descontos:</span>
                    <span>-€{receiptData.sale.discount_total.toFixed(2)}</span>
                  </div>
                )}

                {(receiptData.sale?.voucher_discount || 0) > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>Voucher ({receiptData.sale.voucher_code}):</span>
                    <span>-€{receiptData.sale.voucher_discount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span>IVA:</span>
                  <span>€{(receiptData.sale?.vat_total || 0).toFixed(2)}</span>
                </div>

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Pré-pagamento:</span>
                    <span>-€{Number(receiptData.sale?.prepaid_amount || 0).toFixed(2)}</span>
                  </div>
                )}

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && !!receiptData.sale?.prepaid_method && (
                  <div className="flex justify-between text-gray-700">
                    <span>Método pré-pago:</span>
                    <span>{String(receiptData.sale?.prepaid_method || '').toUpperCase()}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold text-[16px] border-t border-gray-300 pt-1 mt-1">
                  <span>TOTAL:</span>
                  <span>€{(receiptData.sale?.total || 0).toFixed(2)}</span>
                </div>

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && (
                  <div className="flex justify-between font-bold text-[13px]">
                    <span>Falta pagar:</span>
                    <span>
                      €{Number(
                        receiptData.sale?.remaining_paid_at_pos ??
                        Math.max(
                          0,
                          Number(receiptData.sale?.total || 0) - Number(receiptData.sale?.prepaid_amount || 0)
                        )
                      ).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-2 border-t border-gray-300 text-[11px] text-center">
                <p>
                  Pagamento: {
                    {
                      cash: 'Dinheiro',
                      card: 'Cartão',
                      mbway: 'MBWay',
                      multibanco: 'Multibanco',
                      transfer: 'Transferência',
                      account_current: 'Conta Corrente',
                      voucher: 'Voucher',
                      other: 'Outro',
                      credit_card: 'Cartão Crédito'
                    }[receiptData.sale?.payment_method] || receiptData.sale?.payment_method
                  }
                </p>
              </div>

              {(receiptData.sale?.qr_data || receiptData.sale?.atcud || receiptData.sale?.ncp_number) && (
                <div className="mt-3 pt-2 border-t border-gray-300 text-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(receiptData.sale?.qr_data || receiptData.sale?.atcud || receiptData.sale?.ncp_number || '')}`}
                    alt="QR"
                    className="w-28 h-28 mx-auto"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">QR / ATCUD</p>
                </div>
              )}

              <div className="text-center mt-4 pt-2 border-t border-gray-300 text-[10px] leading-tight">
                <p className="italic">{receiptData.company?.receipt_footer_message || 'Obrigado pela sua preferência!'}</p>
                {isFiscalSale ? (
                  <p className="mt-1 text-emerald-700 font-bold text-[10px]">Documento fiscal emitido com sucesso.</p>
                ) : (
                  <p className="mt-1 text-gray-500 text-[10px]">Documento interno.</p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2 px-1">
            <label className="text-slate-700 text-sm">Enviar por email</label>
            <Input
              type="email"
              value={saleEmail}
              onChange={(e) => setSaleEmail(e.target.value)}
              placeholder="Email do cliente"
              className="bg-white border-slate-300 text-slate-900 w-full"
              style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
            />
            <p className="modal-dark-muted">
              Pode usar o email do cliente ou escrever um email manualmente.
            </p>
          </div>

          <DialogFooter className="gap-2 grid grid-cols-1 md:grid-cols-2">
            <Button
              variant="outline"
              onClick={() => setShowReceiptModal(false)}
              className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              Fechar
            </Button>

            <Button
              variant="outline"
              onClick={async () => {
                const email = String(saleEmail || '').trim();
                if (!email) {
                  toast.error('Indique um email para envio');
                  return;
                }
                if (!completedSaleId) {
                  toast.error('Venda sem identificador');
                  return;
                }

                try {
                  setSendingSaleEmail(true);
                  await sendReceiptPreviewEmail(completedSaleId, email);
                  toast.success('Email enviado com sucesso');
                } catch (error) {
                  console.error('Erro ao enviar email:', error);
                  toast.error(error?.response?.data?.detail || error?.message || 'Erro ao enviar email');
                } finally {
                  setSendingSaleEmail(false);
                }
              }}
              disabled={sendingSaleEmail || !completedSaleId}
              className="w-full border-blue-500 text-blue-700 hover:bg-blue-50"
            >
              {sendingSaleEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A enviar...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar por email
                </>
              )}
            </Button>

            {isFiscalSale && completedSaleId && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await salesAPI.getInvoicePDF(completedSaleId, 'a4');
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    toast.success('Fatura fiscal gerada');
                  } catch (error) {
                    console.error('Erro PDF fiscal:', error);
                    toast.error('Erro ao gerar fatura fiscal');
                  }
                }}
                className="w-full border-emerald-500 text-emerald-400 hover:bg-emerald-500/20"
              >
                <FileText className="w-4 h-4 mr-2" />
                Fatura Fiscal
              </Button>
            )}

            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await downloadReceiptPreviewPdf();
                  toast.success('Talão PDF aberto no navegador');
                } catch (error) {
                  console.error('Erro ao gerar talão PDF:', error);
                  toast.error(error?.message || 'Erro ao gerar PDF');
                }
              }}
              className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              <FileText className="w-4 h-4 mr-2" />
              Talão PDF
            </Button>

            <Button
              onClick={async () => {
                try {
                  await openReceiptPreviewPrintWindow();
                } catch (error) {
                  console.error('Erro ao imprimir talão:', error);
                  toast.error(error?.message || 'Erro ao abrir talão para impressão');
                }
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesHistoryPage;
